// ============================================================================
// AuthCallback
// Owner: Claude 3 (Frontend & UX).
// Purpose: handles the IdP redirect-back; exchanges code for tokens.
// URS rows: AUTH-001.
//
// v0.12.9 — robust state-mismatch recovery. The classic CSRF guard error
// ("OIDC state mismatch") also fires legitimately when the user opens the
// SPA in two tabs, when the dev server restarts, or when an old callback
// URL is replayed from history. In those cases we treat it as a stale
// session, clear OIDC-local sessionStorage, and route back to /login so
// the user can re-initiate cleanly instead of staring at a CSRF warning.
// ============================================================================
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { completeLogin, consumeReturnTo } from '../../auth/oidcClient';
import { useSession } from '../../auth/SessionProvider';

const OIDC_STORAGE_KEYS = [
  'edms.oidc.state',
  'edms.oidc.nonce',
  'edms.oidc.codeVerifierId',
  'edms.oidc.returnTo',
];

function isTransientStateError(e: unknown): boolean {
  if (!(e instanceof Error)) return false;
  const m = e.message.toLowerCase();
  return (
    m.includes('state mismatch') ||
    m.includes('no pending oidc flow') ||
    m.includes('unknown or expired codeverifierid') ||
    m.includes('sessionstorage missing')
  );
}

export function AuthCallback() {
  const { t } = useTranslation();
  const { setSession } = useSession();
  const [status, setStatus] = useState<'exchanging' | 'recovering' | 'error'>('exchanging');
  const [message, setMessage] = useState<string>('');
  const ranOnce = useRef(false);

  useEffect(() => {
    // StrictMode runs effects twice in dev — without this guard, the second
    // run sees the consumed sessionStorage state and false-positives as a
    // mismatch.
    if (ranOnce.current) return;
    ranOnce.current = true;

    const params = new URLSearchParams(window.location.search);
    const code = params.get('code');
    const state = params.get('state');
    if (!code || !state) {
      setStatus('error');
      setMessage(t('auth.login_failed'));
      return;
    }
    completeLogin(code, state)
      .then((s) => {
        setSession(s);
        window.location.assign(consumeReturnTo());
      })
      .catch((e) => {
        if (isTransientStateError(e)) {
          OIDC_STORAGE_KEYS.forEach((k) => sessionStorage.removeItem(k));
          setStatus('recovering');
          setMessage(
            t('auth.stale_session', {
              defaultValue:
                'Your sign-in link has expired or was opened in a new tab. Restarting sign-in…',
            }),
          );
          window.setTimeout(() => {
            window.location.replace('/login');
          }, 1500);
          return;
        }
        setStatus('error');
        setMessage(t('auth.login_failed') + (e instanceof Error ? `: ${e.message}` : ''));
      });
  }, [setSession, t]);

  if (status === 'error') {
    return (
      <main role="alert" style={{ padding: 32 }}>
        <h1>{t('auth.callback_heading')}</h1>
        <p>{message}</p>
        <a href="/login">{t('auth.sso_button')}</a>
      </main>
    );
  }

  if (status === 'recovering') {
    return (
      <main role="status" aria-live="polite" style={{ padding: 32 }}>
        <h1>{t('auth.callback_heading')}</h1>
        <p>{message}</p>
      </main>
    );
  }

  return (
    <main role="status" aria-live="polite" style={{ padding: 32 }}>
      <h1>{t('auth.callback_heading')}</h1>
      <p>{t('auth.callback_exchanging')}</p>
    </main>
  );
}
